/**
 * Assessment Storage Service
 * 
 * Handles storage and sync of fitness tests/assessments to Firebase and localStorage.
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  orderBy, 
  limit,
  Timestamp,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import { getCurrentUserId, getAnonymousUser } from './anonymousAuth';
import { storage } from './storage';

export interface FitnessTest {
  id: string;
  userId: string;
  week: number;
  date: Date;
  bodyweight?: number;
  programId?: string;
  // Day 1
  deadlift3RM?: number;
  benchPressBWMaxReps?: number;
  plankMaxTimeSeconds?: number;
  handReleasePushups?: number;
  run1_5MileMinutes?: number;
  run1_5MileSeconds?: number;
  swim500mMinutes?: number;
  swim500mSeconds?: number;
  amrap10Rounds?: number;
  amrap10Reps?: number;
  // Day 2
  ruck6MileMilesCompleted?: number;
  ruck6MileMinutes?: number;
  ruck6MileSeconds?: number;
  amrap10RunRounds?: number;
  amrap10RunReps?: number;
  notes?: string;
  // Allow extra fields from Firebase
  [key: string]: any;
}

/**
 * Save fitness test to Firebase AND localStorage
 * Firebase is primary, localStorage is backup/cache
 */
export async function saveAssessment(test: FitnessTest): Promise<void> {
  try {
    // Ensure user is authenticated and get user ID
    let userId: string;
    try {
      userId = await getAnonymousUser();
      console.log(' User authenticated:', userId);
    } catch (authError) {
      console.error(' Authentication failed:', authError);
      // Fall back to anonymous string for localStorage-only save
      userId = 'anonymous_' + Date.now();
      console.warn(' Using fallback user ID:', userId);
    }
    
    const testWithUser = { ...test, userId };
    
    // Save to localStorage first (immediate, for offline)
    const existing = getAssessmentHistory();
    const existingIndex = existing.findIndex(t => t.id === testWithUser.id);
    
    if (existingIndex >= 0) {
      existing[existingIndex] = testWithUser;
    } else {
      existing.push(testWithUser);
    }
    
    storage.set('fitnessTests', JSON.stringify(existing));
    console.log('� Assessment saved to localStorage:', testWithUser.id);
    
    // Sync to Firebase (primary storage)
    try {
      await saveAssessmentToFirebase(testWithUser);
      console.log('� Assessment synced to Firebase:', testWithUser.id);
    } catch (firebaseError) {
      console.error(' Error syncing to Firebase (non-critical):', firebaseError);
      // Don't throw - data is saved to localStorage successfully
    }
  } catch (error) {
    console.error(' Error saving assessment:', error);
    throw error;
  }
}

/**
 * Get assessment history from localStorage
 */
export function getAssessmentHistory(): FitnessTest[] {
  if (typeof window === 'undefined') {
    console.log('� getAssessmentHistory: SSR environment, returning empty array');
    return [];
  }
  
  try {
    const stored = storage.get('fitnessTests');
    if (!stored) {
      console.log('� No assessment data in localStorage');
      return [];
    }

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      console.warn(' Assessment data corrupted, clearing');
      storage.remove('fitnessTests');
      return [];
    }
    
    // Convert date strings back to Date objects
    const tests = parsed.map((test: any) => ({
      ...test,
      date: typeof test.date === 'string' ? new Date(test.date) : test.date,
    }));
    
    console.log(` Loaded ${tests.length} assessments from localStorage`);
    return tests;
  } catch (error) {
    console.error(' Error loading assessments from localStorage:', error);
    storage.remove('fitnessTests');
    return [];
  }
}

/**
 * Load assessments from Firebase for a specific user
 * Checks BOTH the new path (users/{userId}/assessments) AND old path (assessments collection)
 */
export async function loadAssessmentsFromFirebase(userId: string): Promise<FitnessTest[]> {
  try {
    console.log('� Loading assessments from Firebase for user:', userId);
    
    const allAssessments: FitnessTest[] = [];
    
    // 1. Load from NEW path: users/{userId}/assessments (subcollection)
    try {
      const assessmentsRef = collection(db, 'users', userId, 'assessments');
      const q = query(assessmentsRef, orderBy('date', 'desc'));
      const snapshot = await getDocs(q);
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        allAssessments.push({
          ...data,
          id: doc.id,
          date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
        } as FitnessTest);
      });
      
      console.log(` Loaded ${snapshot.size} assessments from NEW path (users/${userId}/assessments)`);
    } catch (err) {
      console.warn(' Error loading from new path:', err);
    }
    
    // 2. ALSO check OLD path: assessments collection (for migration)
    try {
      const oldAssessmentsRef = collection(db, 'assessments');
      const oldQuery = query(
        oldAssessmentsRef,
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );
      const oldSnapshot = await getDocs(oldQuery);
      
      if (oldSnapshot.size > 0) {
        console.log(` Found ${oldSnapshot.size} assessments in OLD path (assessments collection) - migrating...`);
        
        oldSnapshot.forEach((doc) => {
          const data = doc.data();
          const assessment = {
            ...data,
            id: doc.id,
            date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
          } as FitnessTest;
          
          // Only add if not already in new path (prevent duplicates)
          if (!allAssessments.find(a => a.id === assessment.id)) {
            allAssessments.push(assessment);
          }
        });
      }
    } catch (err) {
      console.warn(' Error loading from old path:', err);
    }
    
    // Sort by date (newest first)
    allAssessments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    console.log(` Total assessments loaded: ${allAssessments.length}`);
    
    // Save to localStorage for offline access
    if (allAssessments.length > 0) {
      storage.set('fitnessTests', JSON.stringify(allAssessments));
      console.log('� Cached assessments to localStorage');
    }
    
    return allAssessments;
  } catch (error) {
    console.error(' Error loading assessments from Firebase:', error);
    // Fall back to localStorage
    return getAssessmentHistory();
  }
}

/**
 * Save fitness test/assessment to Firestore
 */
async function saveAssessmentToFirebase(test: FitnessTest): Promise<void> {
  try {
    const testRef = doc(db, 'users', test.userId, 'assessments', test.id);
    
    // Convert date to Firestore Timestamp
    const testData = {
      ...test,
      date: Timestamp.fromDate(test.date),
      syncedAt: Timestamp.now(),
    };
    
    await setDoc(testRef, testData);
    console.log(' Assessment saved to Firebase:', test.id);
  } catch (error) {
    console.error(' Error saving assessment to Firebase:', error);
    throw error;
  }
}

/**
 * Get assessment history from Firestore
 */
async function getAssessmentHistoryFromFirebase(userId: string, limitCount: number = 20): Promise<FitnessTest[]> {
  try {
    const assessmentsRef = collection(db, 'users', userId, 'assessments');
    const q = query(assessmentsRef, orderBy('date', 'desc'), limit(limitCount));
    
    const snapshot = await getDocs(q);
    const tests: FitnessTest[] = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        date: data.date?.toDate() || new Date(),
      } as FitnessTest;
    });
    
    console.log(` Retrieved ${tests.length} assessments from Firebase`);
    return tests;
  } catch (error) {
    console.error(' Error getting assessments from Firebase:', error);
    return [];
  }
}

/**
 * Sync assessment history from Firebase to localStorage
 * Call this on app initialization
 */
export async function syncAssessmentHistoryFromFirebase(): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId) {
    console.log('� No user ID, skipping Firebase sync');
    return 0;
  }

  try {
    console.log('� Syncing assessments from Firebase...');
    const tests = await getAssessmentHistoryFromFirebase(userId, 50);
    
    if (tests.length > 0) {
      storage.set('fitnessTests', JSON.stringify(tests));
      console.log(` Synced ${tests.length} assessments from Firebase to localStorage`);
    }
    
    return tests.length;
  } catch (error) {
    console.error(' Failed to sync assessments from Firebase:', error);
    return 0;
  }
}

